import React, { useState } from 'react';
import { usePricing } from '../contexts/PricingContext';
import { useDemoMode } from '../contexts/DemoModeContext';
import TournamentStatus from './TournamentStatus';
import logo from '../assets/logo.png';

export const AtticusHeader = ({ 
  onAutoHedgeClick, 
  onEducationalDemoClick,
  onSocialTradingClick,
  onWalkthroughClick,
  onLeaderboardClick,
  socialTradingCanisterId,
  currentUserId,
  userDisplayName
}) => {
  const { isDemoMode, demoPortfolio } = useDemoMode();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Responsive button styling for five-button layout
  const buttonClasses = {
    base: "px-1.5 py-1 text-xs font-medium rounded-md transition-all duration-200 flex items-center gap-1",
    primary: "bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-300 hover:to-yellow-400",
    secondary: "bg-atticus-navy-800 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-400/10"
  };

  return (
    <header className="bg-atticus-navy-950 border-b border-yellow-400/20 px-4 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex items-center">
          <img src={logo} alt="Atticus" className="h-8 w-auto" />
          {isDemoMode && (
            <span className="ml-3 px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-md border border-green-400/30">
              DEMO MODE
            </span>
          )}
        </div>

        {/* Desktop Navigation - Five Button Layout */}
        <div className="hidden md:flex items-center gap-1">
          <button 
            onClick={onEducationalDemoClick}
            className={`${buttonClasses.base} ${buttonClasses.primary}`}
            title="Educational Academy"
          >
            📚
            <span className="hidden sm:inline">Academy</span>
          </button>
          
          <button 
            onClick={onWalkthroughClick}
            className={`${buttonClasses.base} ${buttonClasses.secondary}`}
            title="Guided Walkthrough"
          >
            🎯
            <span className="hidden sm:inline">Walkthrough</span>
          </button>
          
          <button 
            onClick={onSocialTradingClick}
            className={`${buttonClasses.base} ${buttonClasses.secondary}`}
            title="Social Trading & Tournaments"
          >
            🤝
            <span className="hidden sm:inline">Social</span>
          </button>
          
          <button 
            onClick={onLeaderboardClick}
            className={`${buttonClasses.base} ${buttonClasses.secondary}`}
            title="Tournament Leaderboard"
          >
            🏆
            <span className="hidden sm:inline">Leaderboard</span>
          </button>
          
          <button 
            onClick={onAutoHedgeClick}
            className={`${buttonClasses.base} ${buttonClasses.secondary}`}
            title="Auto Hedge"
          >
            ⚡
            <span className="hidden sm:inline">Hedge</span>
          </button>
          
          {/* Tournament Status */}
          <TournamentStatus />
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button 
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="text-yellow-400 p-2"
            aria-label="Toggle mobile menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden mt-3 pb-3 border-t border-yellow-400/20">
          {/* Tournament Status for Mobile */}
          <div className="mb-3">
            <TournamentStatus />
          </div>
          
          <div className="flex flex-col gap-2 mt-3">
            <button 
              onClick={() => {
                onEducationalDemoClick();
                setShowMobileMenu(false);
              }}
              className={`${buttonClasses.base} ${buttonClasses.primary} justify-center`}
            >
              📚 Trading Academy
            </button>
            
            <button 
              onClick={() => {
                onWalkthroughClick();
                setShowMobileMenu(false);
              }}
              className={`${buttonClasses.base} ${buttonClasses.secondary} justify-center`}
            >
              🎯 Guided Walkthrough
            </button>
            
            <button 
              onClick={() => {
                onSocialTradingClick();
                setShowMobileMenu(false);
              }}
              className={`${buttonClasses.base} ${buttonClasses.secondary} justify-center`}
            >
              🤝 Social Trading
            </button>
            
            <button 
              onClick={() => {
                onLeaderboardClick();
                setShowMobileMenu(false);
              }}
              className={`${buttonClasses.base} ${buttonClasses.secondary} justify-center`}
            >
              🏆 Leaderboard
            </button>
            
            <button 
              onClick={() => {
                onAutoHedgeClick();
                setShowMobileMenu(false);
              }}
              className={`${buttonClasses.base} ${buttonClasses.secondary} justify-center`}
            >
              ⚡ Auto Hedge
            </button>
          </div>
        </div>
      )}

      {/* Demo Portfolio Summary (Mobile) */}
      {isDemoMode && demoPortfolio && (
        <div className="md:hidden mt-2 text-xs text-gray-400">
          Balance: ${demoPortfolio.balance?.toLocaleString() || '10,000'} | 
          Positions: {demoPortfolio.positions?.length || 0}
        </div>
      )}
    </header>
  );
};
